import { useEffect, useState } from "react"
import { Box, Skeleton, Stack, Typography } from "@mui/material"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { makeStyles } from "tss-react/mui"
import { tailwindPalette } from "../../../../../theme/tailwindPalette"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faLightbulb } from "@fortawesome/pro-solid-svg-icons"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { fetchAvailableRooms, FetchAvailableRoomsResponse, UserDetails } from "../../../../nonAdminUserApi"
import { faClock } from "@fortawesome/pro-light-svg-icons"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectKitchen } from "../../../../nonAdminUserSelectors"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { ErrorBoundary } from "react-error-boundary"
import { UserDashboardErrorFallbackCard } from "../UserDashboardErrorFallbackCard"
import { DateTimeFactory, useDateTime } from "../../../../../localization/useDateTime"

const useStyles = makeStyles()((theme) => ({
    container: {
        color: theme.palette.common.black,
        backgroundColor: tailwindPalette.purple[200],
        borderRadius: "1rem",
        padding: "1rem",
        marginBottom: "3.75rem",
    },
    section: {
        display: "flex",
        flexDirection: "column",
    },
    leftSection: {
        flex: "65 1 0",
        padding: ".5rem 1rem .5rem .5rem",
        justifyContent: "space-between",
        gap: "1rem",
    },
    leftTop: {
        display: "flex",
        flexDirection: "column",
        gap: ".5rem",
    },
    rightSection: {
        flex: "35 1 0",
        maxWidth: "12.5rem",
        aspectRatio: "6 / 5",
        justifyContent: "center",
        alignItems: "center",
    },
    timeSection: {
        borderRadius: "0.625rem",
        padding: "1.25rem",
        backgroundColor: "#FFFFFF40",
        color: tailwindPalette.purple[900],
    },
    roomFinderButton: {
        border: `1px solid ${tailwindPalette.slate[300]} !important`,
        backgroundColor: theme.palette.common.white,
        color: tailwindPalette.slate[800],
        "&:hover": {
            backgroundColor: tailwindPalette.slate[100],
        },
    },
    schedule: {
        fontSize: "3.75rem",
        fontWeight: 500,
        color: tailwindPalette.purple[900],
        textAlign: "center",
        lineHeight: 1,
    },
    timeSlot: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: ".375rem",
        color: theme.palette.text.secondary,
    },
    timeIcon: {
        width: ".875rem",
        height: ".875rem",
        fontSize: ".875rem",
    },
    skeletonCard: {
        height: "9.2rem",
        borderRadius: ".625rem",
        width: "100%",
    },
    errorCard: {
        backgroundColor: "transparent",
        boxShadow: "none",
    },
    errorContent: {
        paddingLeft: 0,
        paddingRight: 0,
    },
}))

const transformAvailableRooms = (data: FetchAvailableRoomsResponse) => {
    return data.availableRooms ?? {}
}

const getCurrentTimeSlot = (dateTimeFactory: DateTimeFactory) => {
    const now = dateTimeFactory.now()
    const startHour = now.startOf("hour")
    const endHour = startHour.plus({ hours: 1 })

    return {
        timeSlot: `${dateTimeFactory.toHourMinute(startHour)} - ${dateTimeFactory.toHourMinute(endHour)}`,
        startDateTime: startHour.toISO(),
        endDateTime: endHour.toISO(),
    }
}

function SafeFindRoom({ onOpenRoomFinder }: { onOpenRoomFinder: () => void }) {
    const { classes, cx } = useStyles()
    const dateTimeFactory = useDateTime()

    const [timeSlot, setTimeSlot] = useState(getCurrentTimeSlot(dateTimeFactory).timeSlot)
    const [startDateTime, setStartDateTime] = useState<string | null>(getCurrentTimeSlot(dateTimeFactory).startDateTime)
    const [endDateTime, setEndDateTime] = useState<string | null>(getCurrentTimeSlot(dateTimeFactory).endDateTime)

    const selectedKitchen = useAppSelector(selectKitchen)

    useEffect(() => {
        const interval = setInterval(() => {
            const {
                timeSlot: newTimeSlot,
                startDateTime: newStart,
                endDateTime: newEnd,
            } = getCurrentTimeSlot(dateTimeFactory)
            setTimeSlot(newTimeSlot)
            setStartDateTime(newStart)
            setEndDateTime(newEnd)
        }, 60000)

        return () => clearInterval(interval)
    }, [])

    const { data: rooms, isInitialLoading: isLoading } = useApiQuery(fetchAvailableRooms, {
        queryName: UserDetails.USER_AVAILABLE_ROOMS,
        dependencies: {
            locationId: selectedKitchen?.id ?? 0,
            startDateTime: startDateTime,
            endDateTime: endDateTime,
        },
        enabled: Boolean(selectedKitchen?.id),
        select: transformAvailableRooms,
        refetchInterval: 60000,
        useErrorBoundary: true,
    })

    return (
        <>
            <Box className={cx(classes.section, classes.leftSection)}>
                <Box className={classes.leftTop}>
                    <LocalizedStrict id="user-homeroom-finder-title">
                        <Typography variant="h5" fontWeight={500} color="text.primary">
                            Room finder
                        </Typography>
                    </LocalizedStrict>

                    <LocalizedStrict id="user-home-room-finder-subtitle">
                        <Typography variant="body1" color="text.primary">
                            Your intelligent companion for finding resources.
                        </Typography>
                    </LocalizedStrict>
                </Box>
                <Box>
                    <LocalizedStrict id="user-home-room-finder-find-a-room-button">
                        <StandardButton
                            fullWidth={false}
                            className={classes.roomFinderButton}
                            startIcon={<FontAwesomeIcon icon={faLightbulb} />}
                            onClick={onOpenRoomFinder}
                        >
                            Find a room
                        </StandardButton>
                    </LocalizedStrict>
                </Box>
            </Box>

            {isLoading ? (
                <Box className={cx(classes.section, classes.rightSection)}>
                    <Skeleton variant="rectangular" className={classes.skeletonCard} />
                </Box>
            ) : (
                <ErrorBoundary fallback={<UserDashboardErrorFallbackCard />}>
                    <Box className={cx(classes.section, classes.rightSection, classes.timeSection)}>
                        <Stack spacing=".25rem" alignItems="center">
                            <Typography className={classes.schedule}>{rooms?.count || 0}</Typography>
                            <LocalizedStrict id="user-home-room-finder-available-rooms">
                                <Typography fontSize="1rem" fontWeight={500} color="text.primary" textAlign="center">
                                    available rooms
                                </Typography>
                            </LocalizedStrict>
                            <Box className={classes.timeSlot}>
                                <FontAwesomeIcon icon={faClock} className={classes.timeIcon} />
                                <LocalizedStrict id="user-home-room-finder-available-time-slot" vars={{ timeSlot }}>
                                    <Typography variant="body2" color="text.secondary">
                                        from {timeSlot}
                                    </Typography>
                                </LocalizedStrict>
                            </Box>
                        </Stack>
                    </Box>
                </ErrorBoundary>
            )}
        </>
    )
}
export function FindRoom(props: { onOpenRoomFinder: () => void }) {
    const { classes } = useStyles()
    return (
        <Stack direction={{ xs: "column", md: "row" }} className={classes.container} spacing={0}>
            <QueryErrorBoundary Fallback={RoomFinderFallback}>
                <SafeFindRoom {...props} />
            </QueryErrorBoundary>
        </Stack>
    )
}

const RoomFinderFallback = () => {
    const { classes } = useStyles()

    return (
        <Box marginBottom="3.75rem">
            <UserDashboardErrorFallbackCard classNames={classes.errorCard} contentClassNames={classes.errorContent}>
                <LocalizedStrict id="user-homeroom-finder-title">
                    <Typography fontWeight={500} variant="h5">
                        Room finder
                    </Typography>
                </LocalizedStrict>
            </UserDashboardErrorFallbackCard>
        </Box>
    )
}
